import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';

import { gqlClient } from '@/api';

/**
 * The language-model provider the admin talks to, as the server lets a screen
 * see it.
 *
 * ⚠️ **There is no `apiKey` on this type because there is none on the
 * server's.** `apiKeyLast4` is the whole of what comes back about the key. A
 * field added here would be a field the query asks for and the schema refuses,
 * which is the right way round for that to fail.
 */

export type AiProviderName = 'anthropic' | 'openai' | 'gemini' | 'ollama' | 'openrouter' | 'custom';

export interface AdminAiProvider {
  provider: AiProviderName;
  baseUrl: string;
  baseUrlIsDefault: boolean;
  model: string;
  apiKeyLast4: string | null;
  /** A key is stored and the server can open it. */
  configured: boolean;
  /** The server has no encryption key, so nothing can be stored yet. */
  storageUnavailable: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface AiProviderInput {
  provider: AiProviderName;
  /** Absent keeps the stored key; null removes it; a string replaces it. */
  apiKey?: string | null;
  baseUrl?: string | null;
  model: string;
}

export interface AiProviderTest {
  ok: boolean;
  message: string;
  milliseconds: number | null;
}

const FIELDS = `
  provider
  baseUrl
  baseUrlIsDefault
  model
  apiKeyLast4
  configured
  storageUnavailable
  updatedAt
  updatedByName
`;

const ADMIN_AI_PROVIDER = gql`query AdminAiProvider { adminAiProvider { ${FIELDS} } }`;

const UPDATE_AI_PROVIDER = gql`
  mutation UpdateAiProvider($input: AiProviderInput!) {
    updateAiProvider(input: $input) { ${FIELDS} }
  }
`;

const TEST_AI_PROVIDER = gql`
  mutation TestAiProvider { testAiProvider { ok message milliseconds } }
`;

/**
 * The enum comes back as its member name — `ANTHROPIC` — and the screen, the
 * entity and the input all speak lower case. One place to turn it round on the
 * way in, so a comparison like `provider === 'anthropic'` is never quietly
 * false.
 */
const fromServer = (raw: AdminAiProvider): AdminAiProvider => ({
  ...raw,
  provider: raw.provider.toLowerCase() as AiProviderName,
});

export const useAiProvider = () =>
  useQuery({
    queryKey: ['adminAiProvider'],
    queryFn: async () =>
      fromServer(
        (await gqlClient.request<{ adminAiProvider: AdminAiProvider }>(ADMIN_AI_PROVIDER))
          .adminAiProvider,
      ),
  });

export const useSaveAiProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AiProviderInput) =>
      /*
       * Upper-cased on the way out. `AiProviderName` is a GraphQL enum and its
       * members are `ANTHROPIC`, not `anthropic`; the screen keeps the lower
       * case because that is what the query hands back and what the entity
       * stores.
       */
      fromServer(
        (
          await gqlClient.request<{ updateAiProvider: AdminAiProvider }>(UPDATE_AI_PROVIDER, {
            input: { ...input, provider: input.provider.toUpperCase() },
          })
        ).updateAiProvider,
      ),
    onSuccess: (saved) => queryClient.setQueryData(['adminAiProvider'], saved),
  });
};

/**
 * One tiny prompt, sent by the server with the stored key.
 *
 * Not cached and not retried: it costs money per press, and the server already
 * limits how often it may be pressed.
 */
export const useTestAiProvider = () =>
  useMutation({
    mutationFn: async () =>
      (await gqlClient.request<{ testAiProvider: AiProviderTest }>(TEST_AI_PROVIDER))
        .testAiProvider,
  });
